<template>
    <div id="savingsCalculatorInner">
        <div class="visible-lte-large" id="savingsResults">
            <h1>Savings Calculator</h1>
            <p>Enter your averages to calculate how much Gadzoom will save your company in time and overhead.</p>
            <div class="total-savings">
                <div class="savings">{{ ahasSavings + plansSavings | asDollars }}
                    <template v-if="shouldShowPlus">+</template>
                </div>
                <div class="label">Average Savings Per Year</div>
            </div>
            <div class="specific-savings">
                <div class="plan-savings">
                    <div class="label">Plan Savings</div>
                    <div class="savings">{{ plansSavings | asDollars }}
                        <template v-if="shouldShowPlus">+</template>
                    </div>
                </div>
                <div class="aha-savings">
                    <div class="label">Aha Savings</div>
                    <div class="savings">{{ ahasSavings | asDollars }}
                        <template v-if="shouldShowPlus">+</template>
                    </div>
                </div>
            </div>
        </div>
        <div class="calculator">
            <h2>Projects per year</h2>
            <RangePicker :model="projectsPerYear">
                Approximately how many projects are you awarded each year?
            </RangePicker>
            <h2>Plans per project</h2>
            <RangePicker :model="plansPerProject">
                How many plans (<a href="/accident-prevention-plan">Accident Prevention Plans</a>, <a
                href="/quality-control-plan">QC Plans</a>, <a href="/environmental-protection-plan">Environmental
                Plans</a>, <a href="/documents">Demolition Plans, Confined Space Entry Plans, etc.</a>) do you average
                on each project?
            </RangePicker>
            <RangePicker :model="daysPerPlan">
                Average time to produce each plan?
            </RangePicker>
            <RangePicker :model="costPerHourForPlans">
                Average hourly cost for persons creating plans?
            </RangePicker>
            <h2>AHAs per project</h2>
            <RangePicker :model="ahasPerProject">
                How many AHAs do you average on each project?
            </RangePicker>
            <RangePicker :model="daysPerAha">
                Average time to produce each AHA?
            </RangePicker>
            <RangePicker :model="costPerHourForAhas">
                Average hourly cost for persons creating AHAs?
            </RangePicker>
        </div>
        <div class="visible-gt-large-flex sidebar">
            <div id="savingsResults">
                <h1>Savings Calculator</h1>
                <p>Enter your averages to calculate how much Gadzoom will save your company in time and overhead.</p>
                <div class="total-savings">
                    <div class="savings">
                        {{ ahasSavings + plansSavings | asDollars }}<template v-if="shouldShowPlus">+</template>
                    </div>
                    <div class="label">Average Savings Per Year</div>
                </div>
                <div class="specific-savings">
                    <div class="plan-savings">
                        <div class="label">Plan Savings</div>
                        <div class="savings">
                            {{ plansSavings | asDollars }}<template v-if="shouldShowPlus">+</template>
                        </div>
                    </div>
                    <div class="aha-savings">
                        <div class="label">Aha Savings</div>
                        <div class="savings">
                            {{ ahasSavings | asDollars }}<template v-if="shouldShowPlus">+</template>
                        </div>
                    </div>
                </div>
            </div>
            <div class="cta">
                <h3>Sign up for a free demo!</h3>
                <p>Gadzoom is a cloud-based program that makes your company's administrative plans for government
                    construction projects fast, accurate, and efficient.</p>
                <a class="lib-button bg-gold full-width" href="/demo-request">Schedule A Demo Now</a>
            </div>
            <div class="filler"></div>
        </div>
        <div class="visible-lte-large cta">
            <h3>Sign up for a free demo!</h3>
            <p>Gadzoom is a cloud-based program that makes your company's administrative plans for government
                construction projects fast, accurate, and efficient.</p>
            <a class="lib-button bg-gold full-width" href="/demo-request">Schedule A Demo Now</a>
        </div>
    </div>
</template>

<script>
import RangePicker from './RangePicker';

function asDollars(value) {
    return Number.parseFloat(value).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
}

function asDays(value) {
    if (value === 1) {
        return `${value} day`;
    } else {
        return `${value} days`;
    }
}

export default {
    name: 'SavingsCalculator',
    components: {RangePicker},
    data: function () {
        return {
            plansTiers: [
                {name: 'Basic', max: 3, price: 695},
                {name: 'Project', max: 20, price: 1195},
                {name: 'Business', max: 60, price: 2495},
            ],
            ahasTiers: [
                {name: 'Basic', max: 25, price: 695},
                {name: 'Project', max: 50, price: 995},
                {name: 'Business', max: 150, price: 2195},
            ],
            projectsPerYear: {
                min: 0, max: 100, step: 1, value: 5,
                ticks: [0, 25, 50, 75, 100],
            },
            plansPerProject: {
                min: 0, max: 40, step: 1, value: 3,
                ticks: [0, 10, 20, 30, 40],
            },
            daysPerPlan: {
                min: 0, max: 8, step: 0.25, value: 3,
                ticks: [0, 2, 4, 6, 8],
                format: asDays,
            },
            costPerHourForPlans: {
                min: 15, max: 75, step: 1, value: 30,
                ticks: [15, 30, 45, 60, 75],
                format: asDollars,
            },
            ahasPerProject: {
                min: 0, max: 80, step: 1, value: 5,
                ticks: [0, 20, 40, 60, 80],
            },
            daysPerAha: {
                min: 0, max: 8, step: 0.25, value: 1,
                ticks: [0, 2, 4, 6, 8],
                format: asDays,
            },
            costPerHourForAhas: {
                min: 15, max: 75, step: 1, value: 35,
                ticks: [15, 30, 45, 60, 75],
                format: asDollars,
            },
        };
    },
    computed: {
        shouldShowPlus: function () {
            return this.projectsPerYear.value === this.projectsPerYear.max.toString()
                || this.plansPerProject.value === this.plansPerProject.max.toString()
                || this.daysPerPlan.value === this.daysPerPlan.max.toString()
                || this.costPerHourForPlans.value === this.costPerHourForPlans.max.toString()
                || this.ahasPerProject.value === this.ahasPerProject.max.toString()
                || this.daysPerAha.value === this.daysPerAha.max.toString()
                || this.costPerHourForAhas.value === this.costPerHourForAhas.max.toString()
        },
        bestAhasTier: function () {
            for (var i = 0; i < this.ahasTiers.length; i++) {
                var item = this.ahasTiers[i];
                if (item.max >= this.ahasPerYear) {
                    return item;
                }
            }
            return {name: 'Enterprise', isEnterprise: true, price: this.projectsPerYear.value * 1000};
        },
        bestPlansTier: function () {
            for (var i = 0; i < this.plansTiers.length; i++) {
                var item = this.plansTiers[i];
                if (item.max >= this.plansPerYear) {
                    return item;
                }
            }
            return {name: 'Enterprise', isEnterprise: true, price: this.projectsPerYear.value * 1000};
        },
        plansPerYear: function () {
            return this.projectsPerYear.value * this.plansPerProject.value;
        },
        ahasPerYear: function () {
            return this.projectsPerYear.value * this.ahasPerProject.value;
        },
        hoursPerYearForPlans: function () {
            return this.plansPerYear * this.daysPerPlan.value * 8;
        },
        hoursPerYearForAhas: function () {
            return this.ahasPerYear * this.daysPerAha.value * 8;
        },
        plansOverhead: function () {
            return this.hoursPerYearForPlans * this.costPerHourForPlans.value;
        },
        ahasOverhead: function () {
            return this.hoursPerYearForAhas * this.costPerHourForAhas.value;
        },
        gadzoomHoursPerYearForPlans: function () {
            return this.projectsPerYear.value * 8;
        },
        gadzoomHoursPerYearForAhas: function () {
            return this.ahasPerYear * (7 / 60);
        },
        gadzoomPlansLaborOverhead: function () {
            return this.gadzoomHoursPerYearForPlans * this.costPerHourForPlans.value;
        },
        gadzoomAhasLaborOverhead: function () {
            return this.gadzoomHoursPerYearForAhas * this.costPerHourForAhas.value;
        },
        gadzoomPlansOverheadTotal: function () {
            return this.gadzoomPlansLaborOverhead + this.bestPlansTier.price;
        },
        gadzoomAhasOverheadTotal: function () {
            return this.gadzoomAhasLaborOverhead + this.bestAhasTier.price;
        },
        plansSavings: function () {
            if (!this.bestPlansTier.isEnterprise) {
                return this.plansOverhead - this.gadzoomPlansOverheadTotal;
            } else {
                return this.plansOverhead - this.gadzoomPlansLaborOverhead;
            }
        },
        ahasSavings: function () {
            if (!this.bestAhasTier.isEnterprise) {
                return this.ahasOverhead - this.gadzoomAhasOverheadTotal;
            } else {
                return this.ahasOverhead - this.gadzoomAhasLaborOverhead;
            }
        },
    },
    filters: {
        asDollars: asDollars,
        rounded: function (value, digits) {
            return Number.parseFloat(value).toLocaleString('en-US', {maximumFractionDigits: digits});
        },
    },
};
</script>